import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, CalendarDays, Users, Car, Wrench,
  UserCog, Package, BarChart3, Settings, ChevronDown, Bell, Plus,
  MapPin, Menu, X, LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/" },
  { id: "jobcards", label: "Job Cards", icon: FileText, path: "/job-cards" },
  { id: "bookings", label: "Bookings", icon: CalendarDays, path: "/bookings" },
  { id: "customers", label: "Customers", icon: Users, path: "/parties" },
  { id: "vehicles", label: "Vehicles", icon: Car, path: "/vehicles" },
  { id: "services", label: "Services", icon: Wrench, path: "/services" },
  { id: "technicians", label: "Technicians", icon: UserCog, path: "/technicians" },
  { id: "inventory", label: "Inventory", icon: Package, path: "/inventory" },
  { id: "reports", label: "Reports", icon: BarChart3, path: "/reports" },
  { id: "settings", label: "Settings", icon: Settings, path: "/settings" },
];

const Layout = ({ children, setIsAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  const getActiveNav = () => {
    const path = location.pathname;
    if (path === "/") return "dashboard";
    if (path === "/job-cards") return "jobcards";
    if (path.startsWith("/bookings")) return "bookings";
    if (path.startsWith("/parties")) return "customers";
    const match = NAV_ITEMS.find(item => path.startsWith(item.path) && item.path !== "/");
    return match ? match.id : "dashboard";
  };

  const activeNav = getActiveNav();

  return (
    <div className="flex h-screen bg-[#F5F7FA] overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[220px] bg-white flex flex-col shadow-sm transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        {/* Logo */}
        <div className="px-5 pt-5 pb-4 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M13 2L4 14h7l-2 8 9-12h-7l2-8z" fill="#2E7D32" stroke="#2E7D32" strokeWidth="1.5"/></svg>
          </div>
          <div className="leading-tight">
            <p className="text-[15px] font-bold text-[#1E293B]">EV Repair Shop</p>
            <p className="text-[10px] text-[#94A3B8] tracking-wide">Service | Care | Trust</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 pt-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeNav === item.id;
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-[14px] font-medium transition-all ${
                  isActive
                    ? "bg-[#3B82F6] text-white shadow-md shadow-blue-200"
                    : "text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#334155]"
                }`}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2.2 : 1.8} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* User card */}
        <div className="px-3 pb-4 mt-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-profile-card" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-[#F1F5F9] transition-colors">
                <div className="w-9 h-9 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-[13px] font-semibold text-[#1E293B] truncate">{user?.name || "User"}</p>
                  <p className="text-[11px] text-[#94A3B8]">Supervisor</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-[#94A3B8] shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 gap-2">
                <LogOut className="w-4 h-4" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="bg-white border-b border-[#E2E8F0] px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-1.5 -ml-1.5 text-[#64748B]" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div>
              <h1 className="text-[22px] font-bold text-[#1E293B]" style={{ fontFamily: "'Inter', sans-serif" }}>
                Welcome back, {user?.name?.split(" ")[0] || "User"}
              </h1>
              <p className="text-[13px] text-[#94A3B8] -mt-0.5">Here's what's happening at your workshop today.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="location-selector" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-[13px] text-[#475569] hover:bg-[#F8FAFC] transition-colors">
              <MapPin className="w-3.5 h-3.5 text-[#3B82F6]" />
              Location 1
              <ChevronDown className="w-3 h-3 text-[#94A3B8]" />
            </button>
            <button data-testid="notifications-btn" className="relative p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors">
              <Bell className="w-5 h-5 text-[#475569]" />
              <span className="absolute top-1 right-1 w-4 h-4 bg-[#EF4444] text-white text-[10px] font-bold rounded-full flex items-center justify-center">3</span>
            </button>
            <Button
              data-testid="header-new-booking"
              onClick={() => navigate("/bookings/new")}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-semibold gap-1.5 h-9 px-4 rounded-lg shadow-sm"
            >
              <Plus className="w-4 h-4" />
              New Booking
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
